'use client';

import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import type {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  Table as TanStackTable
} from '@tanstack/react-table';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataTablePagination } from './pagination';
import { cn } from '@/lib/utils';

/** A body cell, by its row's id (`getRowId`) and its column's id. */
export interface TableCellRef {
  rowId: string;
  columnId: string;
}

type CellHighlight = 'hover-cell' | 'hover-column' | 'active-cell' | 'active-column';

const HEAD_HIGHLIGHT =
  'transition-colors data-[highlight=hover-column]:bg-gray-100 data-[highlight=active-column]:bg-violet-50 data-[highlight=active-column]:text-violet-700';
const CELL_HIGHLIGHT =
  'transition-colors data-[highlight=hover-column]:bg-gray-50 data-[highlight=hover-cell]:bg-gray-100 data-[highlight=active-column]:bg-violet-50/40 data-[highlight=active-cell]:bg-violet-50';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  // Configuration options
  enableSorting?: boolean;
  enableFiltering?: boolean;
  enablePagination?: boolean;
  enableColumnVisibility?: boolean;
  enableRowSelection?: boolean;
  enableTableHeader?: boolean;
  // Pagination options
  pageSize?: number;
  pageSizeOptions?: number[];
  // Server-side pagination — when set, `data` is the current page only, `rowCount` counts the
  // rows on every page, and page changes are reported via onPaginationChange.
  manualPagination?: boolean;
  rowCount?: number;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  // Filter options
  filterColumn?: string;
  filterPlaceholder?: string;
  // Server-side sorting — when set, the table stops sorting rows itself and defers to
  // whatever order `data` already arrives in, reporting sort changes via onSortingChange
  // instead. Used by tables backed by a paginated API (e.g. the Sources SOP table) where
  // sorting has to happen in the query, not after only the current page has been fetched.
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  // Callbacks
  onRowClick?: (row: TData) => void;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  getRowClassName?: (row: TData) => string;
  // Stable row ids (TanStack getRowId), e.g. so activeCell still finds its row after a refetch
  getRowId?: (row: TData) => string;
  // Cells in these columns light up their whole column as well as their row while hovered
  crosshairColumnIds?: string[];
  // One cell kept lit across its row and column, e.g. the number whose rows were last opened
  activeCell?: TableCellRef | null;
  // A column kept lit top to bottom, e.g. the metric a drill-down panel was opened from
  markedColumnId?: string;
  // Fill the parent's height: the rows scroll under a sticky header and the pagination stays at the bottom
  scrollBody?: boolean;
  // Shown at the end of the pagination row, e.g. how many rows there are in all
  paginationSummary?: React.ReactNode;
  // Custom toolbar
  toolbar?: (table: TanStackTable<TData>) => React.ReactNode;
  // Empty state
  emptyMessage?: string;
  // Loading state
  isLoading?: boolean;
  // Shown in place of the 'Loading...' text while loading, e.g. a spinner
  loadingContent?: React.ReactNode;
  // Custom className
  className?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  enableSorting = true,
  enableFiltering = false,
  enablePagination = true,
  enableColumnVisibility = false,
  enableRowSelection = false,
  enableTableHeader = true,
  pageSize = 10,
  pageSizeOptions = [10, 20, 30, 40, 50],
  manualPagination = false,
  rowCount,
  pagination: controlledPagination,
  onPaginationChange,
  filterColumn,
  filterPlaceholder = 'Filter...',
  manualSorting = false,
  sorting: controlledSorting,
  onSortingChange,
  onRowClick,
  onRowSelectionChange,
  getRowClassName,
  getRowId,
  crosshairColumnIds,
  activeCell,
  markedColumnId,
  scrollBody = false,
  paginationSummary,
  toolbar,
  emptyMessage = 'No results.',
  isLoading = false,
  loadingContent = 'Loading...',
  className
}: DataTableProps<TData, TValue>) {
  const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [hoveredCell, setHoveredCell] = React.useState<TableCellRef | null>(null);

  // The active cell wins over the hovered one, which wins over the marked column; a header cell takes its column's.
  const highlight = (columnId: string, rowId?: string): CellHighlight | undefined => {
    if (activeCell?.columnId === columnId) return activeCell.rowId === rowId ? 'active-cell' : 'active-column';
    if (hoveredCell?.columnId === columnId) return hoveredCell.rowId === rowId ? 'hover-cell' : 'hover-column';
    if (markedColumnId === columnId) return 'active-column';
    return undefined;
  };
  const hover = (cell: TableCellRef) => setHoveredCell(crosshairColumnIds?.includes(cell.columnId) ? cell : null);

  const isSortingControlled = manualSorting && controlledSorting !== undefined && !!onSortingChange;
  const sorting = isSortingControlled ? controlledSorting : internalSorting;
  const handleSortingChange: React.Dispatch<React.SetStateAction<SortingState>> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    if (isSortingControlled) onSortingChange(next);
    else setInternalSorting(next);
  };

  const isPaginationControlled = manualPagination && controlledPagination !== undefined && !!onPaginationChange;
  const handlePaginationChange: React.Dispatch<React.SetStateAction<PaginationState>> = (updater) => {
    if (!manualPagination || !controlledPagination || !onPaginationChange) return;
    onPaginationChange(typeof updater === 'function' ? updater(controlledPagination) : updater);
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(getRowId && { getRowId }),
    // Column sizing
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 30,
      maxSize: 10000
    },
    // Sorting
    ...(enableSorting && {
      onSortingChange: handleSortingChange,
      ...(manualSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() })
    }),
    // Filtering
    ...(enableFiltering && {
      onColumnFiltersChange: setColumnFilters,
      getFilteredRowModel: getFilteredRowModel()
    }),
    // Pagination
    ...(enablePagination &&
      (isPaginationControlled
        ? { manualPagination: true, rowCount, onPaginationChange: handlePaginationChange }
        : {
            getPaginationRowModel: getPaginationRowModel(),
            initialState: {
              pagination: {
                pageSize
              }
            }
          })),
    // Column visibility
    ...(enableColumnVisibility && {
      onColumnVisibilityChange: setColumnVisibility
    }),
    // Row selection
    ...(enableRowSelection && {
      onRowSelectionChange: setRowSelection,
      enableRowSelection: true
    }),
    state: {
      ...(enableSorting && { sorting }),
      ...(enablePagination && isPaginationControlled && { pagination: controlledPagination }),
      ...(enableFiltering && { columnFilters }),
      ...(enableColumnVisibility && { columnVisibility }),
      ...(enableRowSelection && { rowSelection })
    }
  });

  // Handle row selection change callback
  React.useEffect(() => {
    if (enableRowSelection && onRowSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection, enableRowSelection, onRowSelectionChange, table]);

  return (
    <div className={cn(scrollBody && 'flex min-h-0 flex-col', className)}>
      {/* Toolbar */}
      {toolbar && <div className='mb-4'>{toolbar(table)}</div>}

      {/* Table */}
      <Table style={{ tableLayout: 'fixed', width: '100%' }}>
        {enableTableHeader && (
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      // Exposed so callers can target a specific column in CSS
                      // (e.g. to let one column absorb surplus width) without
                      // relying on a brittle :nth-child position.
                      data-column-id={header.column.id}
                      data-highlight={highlight(header.column.id)}
                      className={cn(HEAD_HIGHLIGHT, scrollBody && 'sticky top-0 z-10')}
                      style={{ width: `${header.getSize()}px` }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody onMouseLeave={crosshairColumnIds ? () => setHoveredCell(null) : undefined}>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-48 text-center'>
                {loadingContent}
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                data-active={activeCell?.rowId === row.id || undefined}
                onClick={() => onRowClick?.(row.original)}
                className={cn(
                  'data-[active=true]:bg-violet-50/40',
                  onRowClick && 'cursor-pointer',
                  getRowClassName?.(row.original)
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    data-column-id={cell.column.id}
                    data-highlight={highlight(cell.column.id, row.id)}
                    onMouseEnter={crosshairColumnIds ? () => hover({ rowId: row.id, columnId: cell.column.id }) : undefined}
                    style={{ width: `${cell.column.getSize()}px` }}
                    className={cn('overflow-hidden', CELL_HIGHLIGHT)}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-80 text-center text-xs'>
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {enablePagination && !isLoading && data.length > 0 && (
        <div className={scrollBody ? 'mt-auto shrink-0 pt-3' : 'mt-4'}>
          <DataTablePagination
            table={table}
            pageSizeOptions={pageSizeOptions}
            showRowSelection={enableRowSelection}
            summary={paginationSummary}
          />
        </div>
      )}
    </div>
  );
}
